import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Box,
  Typography, IconButton, CircularProgress, Alert, Stepper, Step, StepLabel, Autocomplete,
  Chip, Divider
} from '@mui/material';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Common specialties, seeded on the frontend so a brand-new clinic (which has
// zero specialties) still gets useful suggestions. Free-typed values are also
// accepted and created automatically on the backend.
const SEED_SPECIALTIES = [
  'General Physician', 'Homeopathy', 'Ayurveda', 'Dermatology', 'Pediatrics',
  'Gynecology', 'Orthopedics', 'ENT', 'Dentistry', 'Cardiology', 'Neurology',
  'Psychiatry', 'Ophthalmology', 'Physiotherapy', 'Dietetics / Nutrition',
  'Gastroenterology', 'Pulmonology', 'Urology', 'Endocrinology', 'Family Medicine',
];

const STEPS = ['About', 'Photos', 'Doctors'];
const emptyDoctor = () => ({ name: '', specialization_input: '', bio: '', email: '', phone: '', available_from: '', available_to: '', experience_years: '', photoFile: null });

// Suggested conditions (seeded on the frontend). Free-typed values are kept too.
const SEED_CONDITIONS = [
  'Cold & Cough', 'Allergies', 'Skin problems', 'Hair fall', 'Migraine',
  'Arthritis / Joint pain', 'Digestive issues', 'Thyroid disorders', 'Diabetes',
  'Hypertension', 'Asthma', 'PCOD / PCOS', 'Anxiety & Stress', 'Sinusitis', 'Piles',
];

// ── Reusable single-image uploader ───────────────────────────────────────────
function ImagePick({ label, value, onChange, companyId }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(endpoints.company.uploadSectionImage(companyId), fd, { headers: authHeaders() });
      onChange(res.data.url);
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>{label}</Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {value && <Box component="img" src={value} alt={label} sx={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #E2E8F0' }} />}
        <Button component="label" variant="outlined" size="small" startIcon={busy ? <CircularProgress size={14} /> : <PlusOutlined />} sx={{ textTransform: 'none', borderRadius: 2 }}>
          {value ? 'Replace' : 'Upload'}
          <input hidden type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
        </Button>
      </Stack>
    </Box>
  );
}

export default function WelcomeSetupModal({ open, onClose }) {
  const companyId = localStorage.getItem('tenant_id');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Prefilled data
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState(''); // hero subtitle / description
  const [about, setAbout] = useState('');
  const [condTagline, setCondTagline] = useState(''); // conditions section tagline
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [gallery, setGallery] = useState([]);
  const [carousel, setCarousel] = useState([]); // photo gallery carousel images
  const [existingDoctors, setExistingDoctors] = useState([]);
  const [newDoctors, setNewDoctors] = useState([emptyDoctor()]);
  const [conditions, setConditions] = useState([]);
  // Keep the untouched section content so we merge (not overwrite) on save.
  const [heroContent, setHeroContent] = useState({});
  const [aboutContent, setAboutContent] = useState({});
  const [conditionsContent, setConditionsContent] = useState({});
  const [galleryContent, setGalleryContent] = useState({});

  // ── Prefill from existing data whenever the modal opens ──
  useEffect(() => {
    if (!open) return;
    if (!companyId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(''); setStep(0); setDone(false);
    (async () => {
      try {
        const [companyRes, sectionsRes, doctorsRes] = await Promise.allSettled([
          axios.get(endpoints.company.updateCompany(companyId), { headers: authHeaders() }),
          axios.get(endpoints.company.websiteSections(companyId), { headers: authHeaders() }),
          axios.get(endpoints.company.doctorList(), { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        if (companyRes.status === 'fulfilled') {
          const c = companyRes.value.data || {};
          setName(c.name || '');
          setPhone(c.contact_phone || '');
          setAddress(c.address || '');
          setLogoUrl(c.branding_logo || '');
        }
        if (sectionsRes.status === 'fulfilled') {
          const arr = Array.isArray(sectionsRes.value.data) ? sectionsRes.value.data : (sectionsRes.value.data?.results || []);
          const byType = {};
          arr.forEach((s) => { byType[s.section_type] = s.content || {}; });
          const hero = byType.hero || {};
          const ab = byType.about || {};
          const cond = byType.conditions || {};
          const gal = byType.gallery || {};
          setHeroContent(hero); setAboutContent(ab); setConditionsContent(cond); setGalleryContent(gal);
          setTagline(hero.tagline || '');
          setDescription(hero.subtext || '');
          setHeroImage(hero.background_image || '');
          setAbout(ab.who_we_are || '');
          setGallery(Array.isArray(ab.gallery_images) ? ab.gallery_images : []);
          setCarousel(Array.isArray(gal.images) ? gal.images : []);
          setConditions(Array.isArray(cond.items) ? cond.items : []);
          setCondTagline(cond.tagline || '');
        }
        if (doctorsRes.status === 'fulfilled') {
          const arr = Array.isArray(doctorsRes.value.data) ? doctorsRes.value.data : (doctorsRes.value.data?.results || []);
          setExistingDoctors(arr);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  const setNewDoc = (i, k, v) => setNewDoctors((ds) => ds.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));

  const finish = async () => {
    setSaving(true); setError('');
    try {
      // 1) Company basics (+ optional new logo → multipart).
      if (logoFile) {
        const fd = new FormData();
        fd.append('name', name.trim());
        fd.append('contact_phone', phone.trim());
        fd.append('address', address.trim());
        fd.append('branding_logo', logoFile);
        await axios.patch(endpoints.company.updateCompany(companyId), fd, { headers: authHeaders() });
      } else {
        await axios.patch(endpoints.company.updateCompany(companyId),
          { name: name.trim(), contact_phone: phone.trim(), address: address.trim() },
          { headers: authHeaders() });
      }

      // 2) Website sections — merge onto existing content so nothing else is lost.
      await axios.post(endpoints.company.websiteSectionsBulk(companyId), {
        sections: {
          hero: { ...heroContent, tagline: tagline.trim(), subtext: description.trim(), background_image: heroImage || heroContent.background_image || '' },
          about: { ...aboutContent, who_we_are: about.trim(), gallery_images: gallery },
          conditions: { ...conditionsContent, items: conditions, tagline: condTagline.trim() },
          gallery: { ...galleryContent, images: carousel },
        },
      }, { headers: authHeaders() });

      // 3) New doctors (specialty auto-created via specialization_input).
      const toCreate = newDoctors.filter((d) => d.name.trim());
      for (const d of toCreate) {
        const base = {
          name: d.name.trim(),
          specialization_input: (d.specialization_input || '').trim(),
          bio: (d.bio || '').trim(),
          email: (d.email || '').trim(),
          phone: (d.phone || '').trim(),
        };
        if (d.available_from) base.available_from = d.available_from;
        if (d.available_to) base.available_to = d.available_to;
        if (d.experience_years !== '' && d.experience_years != null) base.experience_years = Number(d.experience_years);
        if (d.photoFile) {
          // Photo needs multipart — let the browser set the boundary.
          const fd = new FormData();
          Object.entries(base).forEach(([k, v]) => fd.append(k, v));
          fd.append('photo', d.photoFile);
          await axios.post(endpoints.company.doctorList(), fd, { headers: authHeaders() });
        } else {
          await axios.post(endpoints.company.doctorList(), base, { headers: authHeaders() });
        }
      }

      localStorage.setItem(`welcome_seen_${companyId}`, '1');
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const closeAndFlag = () => {
    if (companyId) localStorage.setItem(`welcome_seen_${companyId}`, '1');
    onClose?.();
  };

  const stepValid = useMemo(() => {
    if (step === 0) return name.trim().length > 0;
    return true;
  }, [step, name]);

  return (
    <Dialog open={open} onClose={saving ? undefined : closeAndFlag} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, pb: 1 }}>
        {done ? "You're all set 🎉" : 'Welcome — let’s set up your site'}
        <IconButton size="small" onClick={closeAndFlag} disabled={saving}><CloseOutlined /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : done ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 3, textAlign: 'center' }}>
            <CheckCircleFilled style={{ color: '#16a34a', fontSize: 44 }} />
            <Typography variant="h6" fontWeight={700}>Your details are saved</Typography>
            <Typography variant="body2" color="text.secondary">
              You can fine-tune everything anytime from <strong>Website Content</strong> and <strong>Doctors</strong>, or reopen this guide from the <strong>?</strong> button in the top bar.
            </Typography>
          </Stack>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
              {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
            </Stepper>

            {/* Step 0 — About */}
            {step === 0 && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">Tell patients who you are — this shows on your public site.</Typography>
                <TextField label="Clinic / Company name" required value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
                <TextField label="Tagline" placeholder="e.g. Trusted homeopathy care since 2005" value={tagline} onChange={(e) => setTagline(e.target.value)} fullWidth />
                <TextField label="Description" placeholder="A one-line description shown under your tagline on the homepage" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
                <TextField label="About us" placeholder="A short description of your clinic and approach" value={about} onChange={(e) => setAbout(e.target.value)} fullWidth multiline minRows={3} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Contact phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
                  <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
                </Stack>
              </Stack>
            )}

            {/* Step 1 — Photos */}
            {step === 1 && (
              <Stack spacing={2.5}>
                <Typography variant="body2" color="text.secondary">Add your logo and a few photos. You can skip and add these later.</Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>Logo</Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {(logoFile || logoUrl) && (
                      <Box component="img" src={logoFile ? URL.createObjectURL(logoFile) : logoUrl} alt="logo" sx={{ height: 56, width: 56, objectFit: 'contain', borderRadius: 1.5, border: '1px solid #E2E8F0' }} />
                    )}
                    <Button component="label" variant="outlined" size="small" startIcon={<PlusOutlined />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                      {logoFile || logoUrl ? 'Replace' : 'Upload'}
                      <input hidden type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                    </Button>
                  </Stack>
                </Box>
                <Divider />
                <ImagePick label="Homepage banner" value={heroImage} onChange={setHeroImage} companyId={companyId} />
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>Clinic photos ({gallery.length}/4)</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {gallery.map((url, i) => (
                      <Box key={i} sx={{ position: 'relative' }}>
                        <Box component="img" src={url} alt="" sx={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #E2E8F0' }} />
                        <IconButton size="small" onClick={() => setGallery((g) => g.filter((_, idx) => idx !== i))}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #E2E8F0', p: 0.25 }}>
                          <DeleteOutlined style={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {gallery.length < 4 && (
                      <ImagePick label="" value="" onChange={(url) => setGallery((g) => [...g, url])} companyId={companyId} />
                    )}
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>Photo gallery carousel ({carousel.length}/10)</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>A sliding showcase of clinic/team photos shown on your site.</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {carousel.map((url, i) => (
                      <Box key={i} sx={{ position: 'relative' }}>
                        <Box component="img" src={url} alt="" sx={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #E2E8F0' }} />
                        <IconButton size="small" onClick={() => setCarousel((g) => g.filter((_, idx) => idx !== i))}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #E2E8F0', p: 0.25 }}>
                          <DeleteOutlined style={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {carousel.length < 10 && (
                      <ImagePick label="" value="" onChange={(url) => setCarousel((g) => [...g, url])} companyId={companyId} />
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}

            {/* Step 2 — Doctors */}
            {step === 2 && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Add your doctors. Specialties are created automatically — pick a suggestion or type your own.
                </Typography>
                {existingDoctors.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Already added</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {existingDoctors.map((d) => (
                        <Chip key={d.id} label={`${d.name}${d.specialization_name ? ` · ${d.specialization_name}` : ''}`} size="small" />
                      ))}
                    </Stack>
                  </Box>
                )}
                {newDoctors.map((d, i) => (
                  <Box key={i} sx={{ p: 1.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                      <TextField label="Doctor name" size="small" value={d.name} onChange={(e) => setNewDoc(i, 'name', e.target.value)} fullWidth />
                      <Autocomplete
                        freeSolo options={SEED_SPECIALTIES} value={d.specialization_input}
                        onInputChange={(_, v) => setNewDoc(i, 'specialization_input', v)}
                        sx={{ width: '100%' }}
                        renderInput={(params) => <TextField {...params} label="Specialty" size="small" />}
                      />
                      {newDoctors.length > 1 && (
                        <IconButton size="small" onClick={() => setNewDoctors((ds) => ds.filter((_, idx) => idx !== i))}><DeleteOutlined /></IconButton>
                      )}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                      <TextField label="Email" size="small" value={d.email} onChange={(e) => setNewDoc(i, 'email', e.target.value)} fullWidth />
                      <TextField label="Phone" size="small" value={d.phone} onChange={(e) => setNewDoc(i, 'phone', e.target.value)} fullWidth />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                      <TextField label="Experience (years)" type="number" size="small" value={d.experience_years} onChange={(e) => setNewDoc(i, 'experience_years', e.target.value)} fullWidth inputProps={{ min: 0 }} />
                      <TextField label="Available from" type="time" size="small" InputLabelProps={{ shrink: true }} value={d.available_from} onChange={(e) => setNewDoc(i, 'available_from', e.target.value)} fullWidth />
                      <TextField label="Available to" type="time" size="small" InputLabelProps={{ shrink: true }} value={d.available_to} onChange={(e) => setNewDoc(i, 'available_to', e.target.value)} fullWidth />
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
                      {d.photoFile && <Box component="img" src={URL.createObjectURL(d.photoFile)} alt="" sx={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #E2E8F0' }} />}
                      <Button component="label" variant="outlined" size="small" startIcon={<PlusOutlined />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                        {d.photoFile ? 'Change photo' : 'Doctor photo'}
                        <input hidden type="file" accept="image/*" onChange={(e) => setNewDoc(i, 'photoFile', e.target.files?.[0] || null)} />
                      </Button>
                    </Stack>
                    <TextField label="Short bio" size="small" value={d.bio} onChange={(e) => setNewDoc(i, 'bio', e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1.5 }}
                      placeholder="A line or two about the doctor's experience and approach" />
                  </Box>
                ))}
                <Button startIcon={<PlusOutlined />} onClick={() => setNewDoctors((ds) => [...ds, emptyDoctor()])} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                  Add another doctor
                </Button>

                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>Conditions you treat</Typography>
                  <Autocomplete
                    multiple freeSolo options={SEED_CONDITIONS} value={conditions}
                    onChange={(_, v) => setConditions(v)}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Pick or type a condition, then press Enter" />}
                  />
                  <TextField label="Section tagline" size="small" fullWidth sx={{ mt: 1.5 }}
                    value={condTagline} onChange={(e) => setCondTagline(e.target.value)}
                    placeholder="e.g. Gentle, effective care for common and chronic conditions" />
                  <Typography variant="caption" color="text.secondary">Shown on your website's “Conditions we treat” section.</Typography>
                </Box>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {done ? (
          <Button variant="contained" onClick={onClose}>Go to dashboard</Button>
        ) : loading ? null : (
          <>
            <Button onClick={closeAndFlag} disabled={saving} sx={{ mr: 'auto', color: 'text.secondary' }}>Skip for now</Button>
            {step > 0 && <Button onClick={() => setStep((s) => s - 1)} disabled={saving}>Back</Button>}
            {step < STEPS.length - 1 ? (
              <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!stepValid}>Next</Button>
            ) : (
              <Button variant="contained" onClick={finish} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
                {saving ? 'Saving…' : 'Finish'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
